/**
 * Pure layout helpers for the Outlook-style calendar (OutlookCalendar.tsx).
 * No React, no fetching — easy to unit test and reuse between the pop-out
 * inbox's Calendar module and (later, if wanted) the standalone /calendar
 * page.
 *
 * IMPORTANT — event day/time bucketing never parses Graph's start/end
 * strings with `new Date(iso)`. Graph calendarView is fetched with
 * `Prefer: outlook.timezone="Eastern Standard Time"`, so
 * `event.start.dateTime` is an Eastern-local WALL-CLOCK string with no `Z`
 * suffix (e.g. "2026-09-02T14:30:00"). Feeding that into `new Date(...)`
 * parses it in the BROWSER'S timezone, which silently shifts the displayed
 * hour (and sometimes the day) whenever the browser isn't set to America/
 * New_York. Every helper below reads the Eastern date/time directly out of
 * the string via slicing instead.
 *
 * Grid CELL dates (the calendar's day columns/cells themselves — not event
 * timestamps) are plain local `Date` objects built with local date math
 * (`setDate`/`getFullYear`/etc.), matching the existing convention in
 * client/src/pages/Calendar.tsx. That's fine here: nothing about a bare
 * "this is the Sept 2 column" grid cell depends on a timezone conversion.
 */

export type CalendarView = "day" | "work-week" | "week" | "month";

export interface EventLike {
  id: string;
  /** Eastern-local wall-clock string, no `Z` — see file header. */
  start: { dateTime: string };
  end: { dateTime: string };
  isAllDay: boolean;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** "YYYY-MM-DD" for a grid-cell Date, using LOCAL date parts (see file header). */
export function dateKey(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** "YYYY-MM-DD" out of an Eastern wall-clock event timestamp — pure string slicing, no Date parsing. */
export function eventDateKey(iso: string): string {
  return iso.slice(0, 10);
}

/** Minutes since Eastern local midnight, out of an Eastern wall-clock event timestamp. */
export function minutesFromDayStart(iso: string): number {
  const hh = Number(iso.slice(11, 13));
  const mm = Number(iso.slice(14, 16));
  if (Number.isNaN(hh) || Number.isNaN(mm)) return 0;
  return hh * 60 + mm;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function startOfDay(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}

/** Monday of the week containing `d`. */
function mondayOf(d: Date): Date {
  const day = d.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day;
  return startOfDay(addDays(d, diff));
}

/** Sunday of the week containing `d`. */
function sundayOf(d: Date): Date {
  const day = d.getDay(); // 0=Sun
  return startOfDay(addDays(d, -day));
}

export interface VisibleRange {
  /** Grid-cell dates to render, local-midnight Date objects. */
  days: Date[];
  /** Widened UTC ISO bounds safe to pass to GET /api/calendar/events?start=&end= (padded a day each side to absorb TZ slop — the precise bucketing happens client-side via eventDateKey). */
  fetchStart: string;
  fetchEnd: string;
}

/**
 * Computes the set of grid-cell days for a view anchored on `anchorDate`,
 * plus a safely-widened fetch range for the events query.
 */
export function visibleRange(view: CalendarView, anchorDate: Date): VisibleRange {
  let days: Date[];
  switch (view) {
    case "day":
      days = [startOfDay(anchorDate)];
      break;
    case "work-week": {
      const mon = mondayOf(anchorDate);
      days = Array.from({ length: 5 }, (_, i) => addDays(mon, i));
      break;
    }
    case "week": {
      const sun = sundayOf(anchorDate);
      days = Array.from({ length: 7 }, (_, i) => addDays(sun, i));
      break;
    }
    case "month": {
      const first = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1);
      const gridStart = sundayOf(first);
      days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i)); // fixed 6x7 grid
      break;
    }
  }

  const first = days[0];
  const last = days[days.length - 1];
  const fetchStart = addDays(first, -1);
  const fetchEnd = new Date(addDays(last, 2));
  fetchEnd.setHours(23, 59, 59, 999);

  return { days, fetchStart: fetchStart.toISOString(), fetchEnd: fetchEnd.toISOString() };
}

/**
 * One event's rendering slice on ONE grid-cell day. A multi-day all-day
 * event or an overnight timed event produces one entry PER day it covers
 * (see bucketEventsByDay below) — segmentStartMin/segmentEndMin are
 * minutes-from-midnight CLIPPED to this day's portion, and isContinuation
 * is true for every day after the event's first.
 */
export interface DayBucketEntry<T extends EventLike> {
  event: T;
  segmentStartMin: number;
  segmentEndMin: number;
  isContinuation: boolean;
}

export interface DayBucket<T extends EventLike> {
  allDay: DayBucketEntry<T>[];
  timed: DayBucketEntry<T>[];
}

const MINUTES_PER_DAY = 24 * 60;

/**
 * Buckets events into their grid-cell day(s) (keyed by dateKey), splitting
 * all-day vs timed. Events whose Eastern date doesn't match any day in
 * `days` are dropped (they were only fetched as fetch-range padding).
 *
 * Multi-day handling (both all-day AND overnight timed events land on
 * EVERY day they cover, not just their start day):
 *  - All-day: Graph's `end` is the EXCLUSIVE day after the last day the
 *    event covers (a 3-day OOF Sep 1-3 has end="2026-09-04"), so the loop
 *    below is `[startKey, endKeyExclusive)`.
 *  - Timed: `end` is the actual (inclusive) end instant, so an 11 PM-1 AM
 *    event's loop is `[startKey, endKey]` inclusive, with each day's entry
 *    clipped to that day's [0, 1440) minute range.
 */
export function bucketEventsByDay<T extends EventLike>(events: T[], days: Date[]): Map<string, DayBucket<T>> {
  const map = new Map<string, DayBucket<T>>();
  for (const d of days) map.set(dateKey(d), { allDay: [], timed: [] });

  const push = (key: string, ev: T, segmentStartMin: number, segmentEndMin: number, isContinuation: boolean) => {
    const bucket = map.get(key);
    if (!bucket) return;
    const entry: DayBucketEntry<T> = { event: ev, segmentStartMin, segmentEndMin, isContinuation };
    (ev.isAllDay ? bucket.allDay : bucket.timed).push(entry);
  };

  for (const ev of events) {
    const startKey = eventDateKey(ev.start.dateTime);
    const endKey = eventDateKey(ev.end.dateTime);

    if (ev.isAllDay) {
      // endKey is EXCLUSIVE — loop up to but not including it.
      for (const d of days) {
        const key = dateKey(d);
        if (key >= startKey && key < endKey) {
          push(key, ev, 0, MINUTES_PER_DAY, key !== startKey);
        }
      }
      continue;
    }

    if (endKey === startKey) {
      push(startKey, ev, minutesFromDayStart(ev.start.dateTime), minutesFromDayStart(ev.end.dateTime), false);
      continue;
    }

    // Overnight (or multi-day) timed event — endKey is INCLUSIVE here.
    for (const d of days) {
      const key = dateKey(d);
      if (key < startKey || key > endKey) continue;
      if (key === startKey) {
        push(key, ev, minutesFromDayStart(ev.start.dateTime), MINUTES_PER_DAY, false);
      } else if (key === endKey) {
        push(key, ev, 0, minutesFromDayStart(ev.end.dateTime), true);
      } else {
        push(key, ev, 0, MINUTES_PER_DAY, true);
      }
    }
  }

  return map;
}

export interface OverlapLayout {
  id: string;
  col: number;
  totalCols: number;
}

/**
 * Assigns side-by-side column positions to overlapping timed events within
 * a single day, so the UI can render them as adjacent chips (Outlook-style)
 * rather than stacked on top of each other. Classic interval-graph
 * coloring: events are grouped into overlap clusters, each cluster gets
 * its own column count (the max concurrency within it), and every event in
 * that cluster reports that width so its chip renders at a consistent
 * fractional column even if it personally doesn't touch every other event.
 */
export function layoutOverlaps<T extends EventLike>(dayEvents: DayBucketEntry<T>[]): OverlapLayout[] {
  const items = dayEvents
    .map((entry) => ({
      id: entry.event.id,
      startMin: entry.segmentStartMin,
      endMin: Math.max(entry.segmentEndMin, entry.segmentStartMin + 15),
    }))
    .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);

  const result: OverlapLayout[] = [];
  let clusterItems: typeof items = [];
  let clusterMaxEnd = -Infinity;

  const flushCluster = () => {
    if (clusterItems.length === 0) return;
    // Column assignment within the cluster.
    const colEnds: number[] = []; // colEnds[c] = end minute of the last event placed in column c
    const assigned: { id: string; col: number }[] = [];
    for (const it of clusterItems) {
      let col = colEnds.findIndex((end) => end <= it.startMin);
      if (col === -1) {
        col = colEnds.length;
        colEnds.push(it.endMin);
      } else {
        colEnds[col] = it.endMin;
      }
      assigned.push({ id: it.id, col });
    }
    const totalCols = colEnds.length;
    for (const a of assigned) result.push({ id: a.id, col: a.col, totalCols });
    clusterItems = [];
    clusterMaxEnd = -Infinity;
  };

  for (const it of items) {
    if (clusterItems.length > 0 && it.startMin >= clusterMaxEnd) {
      flushCluster();
    }
    clusterItems.push(it);
    clusterMaxEnd = Math.max(clusterMaxEnd, it.endMin);
  }
  flushCluster();

  return result;
}

export function isSameDayKey(a: Date, b: Date): boolean {
  return dateKey(a) === dateKey(b);
}

export function monthLabel(anchorDate: Date): string {
  return anchorDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}
